import { useContext, useId } from "react";
import { FormattedMessage } from "react-intl";
import classNames from "classnames/bind";
import AppGlobalsContext from "app/AppGlobalsContext";
import { Button } from "app/components/ui/Button/Button";
import { ButtonLink } from "app/components/ui/ButtonLink/ButtonLink";
import { ArrowRightIcon } from "app/icons/navigation";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import { useInlineHtml } from "../hooks/useInlineHtml";
import { useSignupDialog } from "../hooks/useSignupDialog";
import type {
	MembershipAdvantage,
	MembershipOffersColumns,
	MembershipProduct,
	MembershipSignupTexts,
} from "../types";
import { MembershipAdvantages } from "./MembershipAdvantages";
import { MembershipOffers } from "./MembershipOffers";
import { MembershipSignupDialog } from "./MembershipSignupDialog";
import styles from "./MembershipBlock.module.scss";

const cx = classNames.bind(styles);

export type MembershipBlockProps = {
	/** Connected or identified user: shows the offers and a link to all sales instead of the signup pitch. */
	isMember?: boolean;
	/** CMS rich text: HTML paragraphs rendered inline in the heading. */
	title?: string | string[];
	products?: MembershipProduct[];
	/** Benefits listed to visitors. */
	advantages?: MembershipAdvantage[];
	/** Label of the button opening the signup form, for visitors. */
	signupLabel?: string;
	/** Rich text shown next to the signup form. */
	signupTexts?: MembershipSignupTexts;
	/** Columns of the offers grid on desktop. */
	columns?: MembershipOffersColumns;
	/** How far the mobile carousel overflows the block: the inline padding of the parent, so slides reach its edges. */
	bleed?: number | string;
};

/**
 * Members area pitch. Visitors see the benefits and a signup button, members see the
 * private sales and a link to all of them. It takes the full width of its parent, which
 * provides the padding and the dark surface, from the `membership-surface` mixin.
 */
export const MembershipBlock = ({
	isMember = false,
	title,
	products = [],
	advantages = [],
	signupLabel,
	signupTexts,
	columns = 3,
	bleed,
}: MembershipBlockProps) => {
	const { resolution } = useContext(AppGlobalsContext);
	const isMobile = resolution === RESOLUTION.SMALL || resolution === RESOLUTION.MEDIUM;
	const titleId = useId();
	const titleHtml = useInlineHtml(title);
	const signupDialog = useSignupDialog();
	const { track } = useAnalytics();
	const hasTitle = titleHtml.length > 0;

	const trackAllSalesClick = () => {
		track("membership_view_all_sales_click");
	};

	const allSalesLink = (
		<ButtonLink
			variant={isMobile ? "secondary" : "borderless"}
			size="lg"
			fullWidth={isMobile}
			className={cx("membership-block__all-sales")}
			endIcon={<ArrowRightIcon />}
			to={{ pathname: "/listing" }}
			onClick={trackAllSalesClick}
		>
			<FormattedMessage id="membership.all.our.sales" />
		</ButtonLink>
	);

	return (
		<section
			className={cx("membership-block")}
			data-member={isMember}
			aria-labelledby={hasTitle ? titleId : undefined}
			data-testid="membership-block"
		>
			<header className={cx("membership-block__header")}>
				{hasTitle && (
					<Typography
						component="h2"
						id={titleId}
						className={cx("membership-block__title")}
						variant={TYPOGRAPHY_VARIANTS.XL4}
						isBold
						dangerouslySetInnerHTML={{ __html: titleHtml }}
					/>
				)}

				{isMember && !isMobile && allSalesLink}
			</header>

			{!isMember && (
				<>
					{advantages.length > 0 && <MembershipAdvantages advantages={advantages} />}

					<Button
						variant="primary"
						size="lg"
						className={cx("membership-block__signup-button")}
						aria-haspopup="dialog"
						onClick={signupDialog.open}
					>
						{signupLabel}
					</Button>

					<MembershipSignupDialog
						isOpen={signupDialog.isOpen}
						isMobile={isMobile}
						onClose={signupDialog.close}
						texts={signupTexts}
					/>
				</>
			)}

			{products.length > 0 && (
				<MembershipOffers
					products={products}
					isMobile={isMobile}
					columns={columns}
					bleed={bleed}
					labelledBy={hasTitle ? titleId : undefined}
				/>
			)}

			{isMember && isMobile && allSalesLink}
		</section>
	);
};
